import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckoutService from '../../services/CheckoutService';

interface PurchaseState {
  status: 'loading' | 'success' | 'error';
  message?: string;
}

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PurchaseState>({ status: 'loading' });

  const organizationId = searchParams.get('organization_id') ?? '';
  const sessionId = searchParams.get('session_id') ?? '';
  const returnTo = searchParams.get('return_to') ?? '/';

  useEffect(() => {
    if (!organizationId || !sessionId) {
      setState({ status: 'error', message: 'Missing session information.' });
      return;
    }

    (async () => {
      try {
        const checkoutService = new CheckoutService();
        await checkoutService.verifyCheckoutSession(sessionId);
        // TODO: Refresh subscription status via context/global state
        setState({ status: 'success' });
        // Automatically redirect after short delay
        setTimeout(() => {
          navigate(returnTo, { state: { purchaseSuccess: true } });
        }, 1500);
      } catch (err: any) {
        console.error('Purchase verification failed', err);
        setState({ status: 'error', message: 'Unable to verify payment. Please contact support.' });
      }
    })();
  }, [organizationId, sessionId, navigate, returnTo]);

  if (state.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <Alert severity="error">{state.message}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 500,
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Payment Successful!
        </Typography>
        <Typography variant="body1" paragraph>
          Thank you for your purchase. Your credits have been added to your account.
        </Typography>

      </Paper>
    </Box>
  );
};

export default SubscriptionSuccess;